import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { signOut, startAxonLogin } from '../auth/axon-auth';
import {
  clearToken,
  createAuthProvider,
  endSession,
  fetchCapabilities,
  fetchCurrentUser,
  ForbiddenError,
  restoreSession,
} from '../auth/session';
import type { Theme } from '../chat/theme';
import { ThemeToggle } from '../Settings';
import '../App.css';
// Lazy so the admin entry (its own Preact + client runtime) is a separate chunk
// that only /admin fetches — see the matching note in App.tsx.
const AxonChatAdmin = lazy(() =>
  import('./AxonChatAdmin').then((m) => ({ default: m.AxonChatAdmin })),
);

type Status =
  | { kind: 'loading' }
  | { kind: 'ready' }
  | { kind: 'not-admin' }
  | { kind: 'forbidden'; message: string }
  | { kind: 'error'; message: string };

/**
 * The `/admin` page: the same Axon auth gate as `App`, then one more check —
 * `GET /capabilities` must report `is_chat_admin`. Only then is
 * `<axon-chat-admin>` mounted. The element repeats the check itself; this
 * page's gate is the door, the element's is the floor.
 */
export function AdminApp() {
  const [status, setStatus] = useState<Status>({ kind: 'loading' });
  const [theme, setTheme] = useState<Theme>('dark');

  const load = useCallback(async () => {
    setStatus({ kind: 'loading' });
    try {
      // No session, in memory or in the refresh cookie → straight to the Axon
      // IdP, as on the chat page.
      if (!(await restoreSession())) {
        await startAxonLogin();
        return;
      }
      const user = await fetchCurrentUser();
      if (!user) {
        clearToken();
        await startAxonLogin();
        return;
      }
      const capabilities = await fetchCapabilities();
      if (!capabilities) {
        clearToken();
        await startAxonLogin();
        return;
      }
      // Not an administrator is terminal, not a sign-in problem: a fresh
      // credential for the same user carries the same scopes, so redirecting
      // to login would only loop. Keep the token — the chat page still works.
      setStatus(capabilities.is_chat_admin ? { kind: 'ready' } : { kind: 'not-admin' });
    } catch (e) {
      if (e instanceof ForbiddenError) {
        // Authenticated but no chat access (403) — same terminal treatment as
        // the chat page.
        await endSession();
        setStatus({ kind: 'forbidden', message: e.message });
        return;
      }
      setStatus({ kind: 'error', message: e instanceof Error ? e.message : String(e) });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Set by Sign out. Clearing the token makes the element's next credential
  // request come back empty, which reports the session as ended; that must not
  // start a second sign-in racing the one Sign out starts.
  const signingOut = useRef(false);

  const onAuthRejected = useCallback(() => {
    // The element rejected auth mid-session (401) → re-authenticate.
    if (signingOut.current) return;
    clearToken();
    setStatus({ kind: 'loading' });
    void startAxonLogin();
  }, []);

  const auth = useMemo(() => createAuthProvider(onAuthRejected), [onAuthRejected]);

  const signedIn = status.kind === 'ready' || status.kind === 'not-admin';

  return (
    <div className="app" data-theme={theme}>
      <header className="app__bar">
        <span className="app__brand">Axon Chat — Administration</span>
        <div className="app__settings">
          <a className="app__link" href="/">
            Chat
          </a>
          <ThemeToggle value={theme} onChange={setTheme} />
          {signedIn && (
            <button
              type="button"
              className="app__btn"
              onClick={() => {
                signingOut.current = true;
                setStatus({ kind: 'loading' });
                // Only the redirect to the IdP can fail here; endSession never
                // rejects, so the session is already gone.
                signOut().catch((e: unknown) => {
                  // Still on this page: a later rejection is real again.
                  signingOut.current = false;
                  setStatus({ kind: 'error', message: e instanceof Error ? e.message : String(e) });
                });
              }}
            >
              Sign out
            </button>
          )}
        </div>
      </header>
      <main className="app__stage">
        {/* loading: render nothing — brief token-check / redirect gap */}
        {status.kind === 'not-admin' && (
          <div className="center">
            <p>Your account isn&apos;t a chat administrator.</p>
          </div>
        )}
        {status.kind === 'forbidden' && (
          <div className="center">
            <p>{status.message}</p>
          </div>
        )}
        {status.kind === 'error' && (
          <div className="center">
            <p>Couldn&apos;t reach chat: {status.message}</p>
            <button type="button" className="app__btn" onClick={() => void load()}>
              Retry
            </button>
          </div>
        )}
        {status.kind === 'ready' && (
          <Suspense fallback={null}>
            <AxonChatAdmin auth={auth} theme={theme} />
          </Suspense>
        )}
      </main>
    </div>
  );
}
